import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCounsellorDetQuery } from '../../store/api/userApiSlice';
import CounsellorFilterPanel from './CounsellorFilterPanel';
import '../CollegeFinder/CollegeSearchResults.css'; // Reuse existing CSS
import { IoSearch, IoChevronDown } from 'react-icons/io5';
import { MdKeyboardDoubleArrowUp } from "react-icons/md";
import CustomDropdown from '../CollegeFinder/CustomDropdown';
import CounselorCard from './CounselorCard';
import '../../pages/Counsellor.css'; // Import page styles for grid

const CounsellorSearchResults = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const { data, isLoading } = useCounsellorDetQuery();
  // Robust check for different API response structures
  const counselors = data?.counsellors || data?.counsellor || data?.data || (Array.isArray(data) ? data : []) || [];

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [sortBy, setSortBy] = useState('Popularity');
  const [displayCount, setDisplayCount] = useState(12); // Default to 12 for grid
  const [activeFilters, setActiveFilters] = useState({});

  useEffect(() => {
    const queryFromUrl = searchParams.get('q') || '';
    setSearchQuery(queryFromUrl);
  }, [searchParams]);

  const filteredCounselors = counselors.filter(counsellor => {
    // 1. Search Query Logic
    let matchesSearch = true;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = counsellor.name?.toLowerCase().includes(query) || false;
      const roleMatch = counsellor.role?.toLowerCase().includes(query) || false;
      const specMatch = counsellor.specialization?.toLowerCase().includes(query) || false;
      matchesSearch = nameMatch || roleMatch || specMatch;
    }

    // 2. Filter Logic
    let matchesFilters = true;
    if (activeFilters) {
      // Experience Filter
      if (activeFilters.experience) {
        const exp = parseInt(counsellor.yearExp) || 0;
        let [min, max] = [0, 100];
        if (activeFilters.experience === "0-2 Years") { min = 0; max = 2; }
        else if (activeFilters.experience === "2-5 Years") { min = 2; max = 5; }
        else if (activeFilters.experience === "5-10 Years") { min = 5; max = 10; }
        else if (activeFilters.experience === "10+ Years") { min = 10; max = 100; }
        
        if (exp < min || exp > max) matchesFilters = false;
      }

      // Specialization Filter (Checking role or specialization field)
      if (matchesFilters && activeFilters.specialization) {
         const role = (counsellor.role || "").toLowerCase();
         const spec = (counsellor.specialization || "").toLowerCase();
         const filterSpec = activeFilters.specialization.toLowerCase();
         // Loose matching since roles might vary slightly
         if (!role.includes(filterSpec) && !spec.includes(filterSpec)) {
            matchesFilters = false;
         }
      }

      // Fees Filter
      if (matchesFilters && activeFilters.fees) {
        const fee = parseInt(counsellor.fee || counsellor.fees || 0); // Assuming fee field exists
        if (activeFilters.fees === "Free") {
           if (fee > 0) matchesFilters = false;
        } else if (activeFilters.fees === "Under ₹500") {
           if (fee >= 500) matchesFilters = false;
        } else if (activeFilters.fees === "₹500 - ₹1000") {
           if (fee < 500 || fee > 1000) matchesFilters = false;
        } else if (activeFilters.fees === "₹1000 - ₹2000") {
           if (fee < 1000 || fee > 2000) matchesFilters = false;
        } else if (activeFilters.fees === "Above ₹2000") {
           if (fee <= 2000) matchesFilters = false;
        }
      }

      // Rating Filter
      if (matchesFilters && activeFilters.rating) {
        const rating = parseFloat(counsellor.rating) || 0;
        const required = parseFloat(activeFilters.rating.split(' ')[0]) || 0;
        if (rating < required) matchesFilters = false;
      }
    }

    return matchesSearch && matchesFilters;
  });

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    setDisplayCount(12);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 12); // Increment by grid rows
  };

  return (
    <div className="search-results-container">
      <div className="search-sidebar">
        <CounsellorFilterPanel onApply={handleApplyFilters} />
      </div>

      <div className="search-content">
        <div className="search-results-header-container">
          <h2 className="search-results-title">
            Search results {filteredCounselors.length > 0 && `(${filteredCounselors.length})`}
          </h2>
          <div className="search-controls-wrapper">
            <div className="search-bar-wrapper">
              <IoSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search counsellors..." 
                className="college-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="sort-wrapper">
              <div style={{ width: '180px' }}>
                <CustomDropdown 
                  options={['Popularity', 'Rating', 'Experience']}
                  value={sortBy}
                  onChange={setSortBy}
                  placeholder="SORT BY"
                />
              </div>
            </div>
          </div>
        </div>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Searching counsellors...</p>
          </div>
        )}

        {!isLoading && filteredCounselors.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No counsellors found. Try adjusting your search.</p>
          </div>
        )}

        {!isLoading && filteredCounselors.length > 0 && (
          <>
            <div className="counselor-card-grid">
              {filteredCounselors.slice(0, displayCount).map((counselor) => (
                <CounselorCard key={counselor._id || counselor.id} counselor={counselor} />
              ))}
            </div>
            
            <div className="search-footer-actions">
              {displayCount < filteredCounselors.length && (
                <button 
                  className="load-more-btn" 
                  onClick={handleLoadMore}
                >
                  LOAD MORE <IoChevronDown />
                </button>
              )}
              <button className="back-to-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                BACK TO TOP <MdKeyboardDoubleArrowUp />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CounsellorSearchResults;
